/**
 * Content panel for a single selected flight.
 * Displays message data, zoom controls, team toggle, and prediction cards.
 * Rendered inside SidePanel for the active flight tab.
 */

import type { MutableRefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import { useAppStore, getFlightColor } from '../store/appStore';
import { IconMapping } from '../constants/iconMapping';
import {
  zoomToBalloon,
  zoomToNormalLanding,
  zoomToInstantLanding,
  zoomToTrajectory,
} from '../utils/cameraHelpers';
import { fetchPredictions } from '../hooks/usePolling';
import { STATUS_MAP } from '../constants/statusMapping';
import { formatCoords } from '../utils/formatCoords';
import styles from './FlightTab.module.css';

interface Props {
  flightId: number;
  mapRef: MutableRefObject<MapRef | null>;
}

/** Formats an ISO 8601 UTC timestamp as "DD/MM/YYYY HH:MM:SS UTC". */
function formatUTC(ts: string): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} UTC`
  );
}

/** Formats an ISO 8601 UTC timestamp as "HH:MM UTC". */
function formatTimeUTC(ts: string): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}

/**
 * Per-flight panel tab content — message data, zoom, teams toggle, prediction cards.
 * @param flightId - ID of the flight whose data to display
 * @param mapRef   - ref to the react-map-gl MapRef for zoom operations
 */
export default function FlightTab({ flightId, mapRef }: Props) {
  const {
    flights,
    messages,
    predictions,
    flightPrefs,
    selectedMessageIndex,
    setFlightPref,
    setSelectedMessageIndex,
  } = useAppStore();

  const flight = flights[flightId];
  const fp = flightPrefs[flightId];
  const flightMessages = messages[flightId] ?? [];
  const color = getFlightColor(flightId);

  const msgIdx = selectedMessageIndex[flightId] ?? null;
  const isHistorical = msgIdx !== null;
  const displayedMsg = isHistorical ? flightMessages[msgIdx] : flightMessages.at(-1);

  const flightPred = predictions[flightId] ?? {};
  // Data availability — controls zoom buttons enabled state.
  const hasNormalData = !!flightPred.normal;
  const hasInstantData = !!flightPred.instant;
  // Encart (card) visibility — data must exist AND the per-flight checkbox must be ON (§18).
  // Same AND logic as the map layers (§D WEB_TRACKER_REFERENCE_MAP.md).
  const showNormalCard = hasNormalData && fp.showNormal;
  const showInstantCard = hasInstantData && fp.showInstant;

  if (!flight || !fp) return null;

  const VisIcon = fp.isVisible ? IconMapping.panel_flight_visible : IconMapping.panel_flight_hidden;
  const TeamsIcon = IconMapping.panel_teams_toggle;
  const BackIcon = IconMapping.panel_back_to_last;
  const HistoricalIcon = IconMapping.badge_historical;
  const LastIcon = IconMapping.badge_active;
  const GmapsIcon = IconMapping.prediction_gmaps;

  /**
   * Handles prediction checkbox toggle.
   * When turned ON: updates the pref immediately then fires a one-off fetch
   * so the user sees prediction data without waiting for the next polling cycle.
   * When turned OFF: only updates the pref.
   */
  const handlePredToggle = (key: 'showNormal' | 'showInstant', checked: boolean) => {
    setFlightPref(flightId, key, checked);
    if (checked) {
      void fetchPredictions(flightId).catch(() => undefined);
    }
  };

  return (
    <div className={styles.tab}>

      {/* ── Header ──────────────────────────────────────── */}
      <div className={styles.header} data-tour="tour-flight-header">
        <div className={styles.headerInfo}>
          <span className={styles.flightTitle}>
            {flight.separator_name} — Vol #{flight.id}
          </span>
          {flight.end_time && (
            <span className={styles.badgeTerminated}>VOL TERMINÉ</span>
          )}
        </div>
        <div className={styles.headerActions}>
          <button
            className={styles.iconBtn}
            onClick={() => setFlightPref(flightId, 'isVisible', !fp.isVisible)}
            title={fp.isVisible ? 'Masquer sur la carte' : 'Afficher sur la carte'}
          >
            <VisIcon size={15} />
          </button>
          <span className={styles.colorDot} style={{ background: color }} />
        </div>
      </div>

      {/* ── Position badge ───────────────────────────────── */}
      <div className={styles.positionRow}>
        {isHistorical ? (
          <span className={styles.badgeHistorical}>
            <HistoricalIcon size={12} />
            &nbsp;Position historique
          </span>
        ) : (
          <span className={styles.badgeLast}>
            <LastIcon size={12} />
            &nbsp;Dernière position
          </span>
        )}
        {isHistorical && (
          <button
            className={styles.backBtn}
            onClick={() => setSelectedMessageIndex(flightId, null)}
            title="Revenir à la dernière position"
            data-tour="tour-back-button"
          >
            <BackIcon size={13} />
            &nbsp;Revenir
          </button>
        )}
      </div>

      {/* ── Message data ─────────────────────────────────── */}
      {displayedMsg ? (
        <div className={styles.dataBlock} data-tour="tour-frame-panel">
          <div className={styles.timestamp}>{formatUTC(displayedMsg.timestamp)}</div>
          <table className={styles.dataTable}>
            <tbody>
              {displayedMsg.latitude != null && displayedMsg.longitude != null && (() => {
                const { display, gmapsUrl } = formatCoords(displayedMsg.latitude, displayedMsg.longitude);
                return (
                  <tr>
                    <td className={styles.dataLabel}>Position</td>
                    <td className={styles.dataValue}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {display}
                        <a
                          href={gmapsUrl}
                          target="_blank"
                          rel="noreferrer"
                          className={styles.gmapsLink}
                          title="Ouvrir dans Google Maps"
                        >
                          <GmapsIcon size={12} />
                        </a>
                      </span>
                    </td>
                  </tr>
                );
              })()}
              <tr>
                <td className={styles.dataLabel}>Altitude</td>
                <td className={styles.dataValue}>{displayedMsg.altitude.toFixed(0)} m</td>
              </tr>
              <tr>
                <td className={styles.dataLabel}>Sep. alt.</td>
                <td className={styles.dataValue}>{displayedMsg.sep_altitude.toFixed(0)} m</td>
              </tr>
              <tr>
                <td className={styles.dataLabel}>Vitesse</td>
                <td className={styles.dataValue}>{displayedMsg.speed.toFixed(1)} m/s</td>
              </tr>
              <tr>
                <td className={styles.dataLabel}>Température</td>
                <td className={styles.dataValue}>{displayedMsg.Temp.toFixed(1)} °C</td>
              </tr>
              <tr>
                <td className={styles.dataLabel}>Pression</td>
                <td className={styles.dataValue}>{displayedMsg.Plev.toFixed(0)} mbar</td>
              </tr>
              <tr>
                <td className={styles.dataLabel}>Batterie</td>
                <td className={styles.dataValue}>{displayedMsg.u_bat.toFixed(1)} V</td>
              </tr>
              <tr>
                <td className={styles.dataLabel}>Satellites</td>
                <td className={styles.dataValue}>{displayedMsg.n_sats}</td>
              </tr>
              <tr>
                <td className={styles.dataLabel}>Statut</td>
                <td className={styles.dataValue}>
                  {(() => {
                    const entry = STATUS_MAP[displayedMsg.status];
                    if (!entry) return displayedMsg.status;
                    const { label, Icon } = entry;
                    return Icon
                      ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon size={13} />{label}</span>
                      : label;
                  })()}
                </td>
              </tr>
              <tr>
                <td className={styles.dataLabel}>Timer</td>
                <td className={styles.dataValue}>{displayedMsg.timer} min.</td>
              </tr>
              {displayedMsg.payload && (
                <tr>
                  <td className={styles.dataLabel}>Payload</td>
                  <td className={styles.dataValue}>{displayedMsg.payload}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={styles.noData}>Aucune donnée reçue</div>
      )}

      {/* ── Zoom buttons ─────────────────────────────────── */}
      <div className={styles.section} data-tour="tour-zoom-buttons">
        <div className={styles.sectionTitle}>Zoom sur</div>
        <div className={styles.zoomGrid}>
          <button
            className={styles.zoomBtn}
            onClick={() => zoomToBalloon(flightId, messages, mapRef)}
            title="Centrer sur le ballon"
          >
            <IconMapping.panel_zoom_balloon size={13} />
            &nbsp;Ballon
          </button>
          <button
            className={styles.zoomBtn}
            onClick={() => zoomToTrajectory(flightId, messages, mapRef)}
            title="Voir la trajectoire complète"
          >
            <IconMapping.panel_zoom_trajectory size={13} />
            &nbsp;Trajectoire
          </button>
          <button
            className={styles.zoomBtn}
            disabled={!hasNormalData}
            onClick={() => zoomToNormalLanding(flightId, predictions, mapRef)}
            title="Zoom prédiction normale"
          >
            <IconMapping.panel_zoom_normal size={13} />
            &nbsp;Préd. norm.
          </button>
          <button
            className={styles.zoomBtn}
            disabled={!hasInstantData}
            onClick={() => zoomToInstantLanding(flightId, predictions, mapRef)}
            title="Zoom prédiction instantanée"
          >
            <IconMapping.panel_zoom_instant size={13} />
            &nbsp;Préd. inst.
          </button>
        </div>
      </div>

      {/* ── Teams toggle ─────────────────────────────────── */}
      <div className={styles.section} data-tour="tour-teams-button">
        <div className={styles.toggleRow}>
          <TeamsIcon size={14} />
          <span className={styles.toggleLabel}>Équipes</span>
          <button
            className={`${styles.toggle} ${fp.showTeams ? styles.toggleOn : ''}`}
            onClick={() => setFlightPref(flightId, 'showTeams', !fp.showTeams)}
          >
            {fp.showTeams ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>

      {/* ── Predictions ──────────────────────────────────── */}
      {/* Section visible whenever any prediction data exists (to keep checkboxes accessible). */}
      {(hasNormalData || hasInstantData) && (
        <div className={styles.section} data-tour="tour-predictions-section">
          <div className={styles.sectionTitle}>Prédictions</div>

          <div className={styles.predToggles}>
            <label className={styles.predToggle}>
              <input
                type="checkbox"
                checked={fp.showNormal}
                onChange={(e) => handlePredToggle('showNormal', e.target.checked)}
              />
              &nbsp;Normale
            </label>
            <label className={styles.predToggle}>
              <input
                type="checkbox"
                checked={fp.showInstant}
                onChange={(e) => handlePredToggle('showInstant', e.target.checked)}
              />
              &nbsp;Instantanée
            </label>
          </div>

          {/* Cards: data must exist AND checkbox must be ON (§18 AND logic, mirrors map layers §D) */}
          {showNormalCard && flightPred.normal && (
            <div className={styles.predCard} data-tour="tour-prediction-landing-card">
              <div className={styles.predCardTitle}>Normale</div>
              <div className={styles.predCoords}>
                {flightPred.normal.latitude.toFixed(3)}°N&nbsp;
                {flightPred.normal.longitude.toFixed(3)}°E
                <a
                  href={`https://maps.google.com/?q=${flightPred.normal.latitude},${flightPred.normal.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.gmapsLink}
                >
                  <GmapsIcon size={12} />
                </a>
              </div>
              <div className={styles.predCity}>
                {flightPred.normal.city} ({flightPred.normal.city_distance_km.toFixed(1)} km)
              </div>
              <div className={styles.predTime}>{formatTimeUTC(flightPred.normal.landing_time)}</div>
            </div>
          )}

          {showInstantCard && flightPred.instant && (
            <div className={styles.predCard}>
              <div className={styles.predCardTitle}>Instantanée</div>
              <div className={styles.predCoords}>
                {flightPred.instant.latitude.toFixed(3)}°N&nbsp;
                {flightPred.instant.longitude.toFixed(3)}°E
                <a
                  href={`https://maps.google.com/?q=${flightPred.instant.latitude},${flightPred.instant.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.gmapsLink}
                >
                  <GmapsIcon size={12} />
                </a>
              </div>
              <div className={styles.predCity}>
                {flightPred.instant.city} ({flightPred.instant.city_distance_km.toFixed(1)} km)
              </div>
              <div className={styles.predTime}>{formatTimeUTC(flightPred.instant.landing_time)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
